import axios, { AxiosError, AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, getContext, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'
import { i18n } from 'i18next'

import { apiCall } from '../../utils/api'
import { getCountryCode, getMaximumNumberOfSupportedItems, getRedirectToCartOnItemAdded } from '../config/selectors'
import { DialogActions } from '../dialog'
import { GtmEventType } from '../gtm/types'
import { addGtmEvent } from '../gtm/utils'
import { withLoading, withLoadingParametrized } from '../loading'
import { LogActions } from '../logs'
import { Routes } from '../../routes'
import { isUserLoggedIn } from '../user/selectors'
import { mapActionError } from '../../utils/helpers'
import { generateRoute } from '../../utils/routes'
import { selectSorting } from '../table/selectors'
import { ColumnSorting, TableActions, sortParam } from '../table'
import { Dictionary } from '../../utils/types'

import { OrderActions } from './actions'
import { getNumberOfOrderItems, getOrderId, getOrderPageSize } from './selectors'
import {
  AddItemToCartGtmData,
  AddItemToCartResponse,
  OrderData,
  OrderItem,
  OrderItemStatusCode,
  OrderStatus,
  OrderType,
  OrderMetadata,
  PaymentStatus
} from './types'
import { generateAddToCartLoadingId, generateRemoveOrderItemLoadingId } from './utils'

type OrderResponseItem = {
  companyIdentifier?: string
  companyName: string
  countryCode: string
  currency: string
  deliveryOptionCode: string
  discount: number
  discountFormatted: string
  entityIdentifierEncoded: string
  languageCode: string
  orderId: string
  customerRequestId: string
  productCode: string
  netPrice: number
  netValue: number
  taxValue: number
  netValueFormatted: string
  taxValueFormatted: string
  vat: number
  errorCode: string
  statusCode: string
  lastUpdateDate?: string
  documentDate?: string
  valueWithTaxFormatted: string
  valueWithTax: number
  reportName: string
  identifierType: string
  identifier: string
}

export type OrderResponse = {
  data?: {
    order?: {
      orderId: string
      orderStatus: string
      orderType: string
      orderDate?: string
      paymentStatus?: string
      paymentMethod?: string
      currency: string
      totalNetPrice: number
      totalNetValue: number
      totalTaxValue: number
      totalNetValueFormatted: string
      totalTaxValueFormatted: string
      totalValueWithTax: number
      totalValueWithTaxFormatted: string
      customerRequests: OrderResponseItem[]
      accountingError?: OrderResponseError
      isAnyCustomerRequestError?: boolean
    }
    meta?: {
      totalCount: number
      pageCount: number
      pageNumber: number
    }
  }
}

export type OrderResponseError = {
  data: {
    code: string
    description: string
  }
}

export function mapOrderResponseItemsToOrderItems(items?: OrderResponseItem[]): OrderItem[] {
  if (!items) return []
  return _.map(items, x => ({
    customerRequestId: x.customerRequestId,
    companyName: x.companyName,
    countryCode: x.countryCode,
    orderId: x.customerRequestId,
    reportType: x.productCode,
    companyId: x.entityIdentifierEncoded,
    deliveryOptionCode: x.deliveryOptionCode,
    languageCode: x.languageCode,
    netValueFormatted: x.netValueFormatted,
    errorCode: _.replace(x.errorCode, '.', ':'),
    companyIdentifier: x.companyIdentifier,
    lastUpdateDate: x.lastUpdateDate,
    documentDate: x.documentDate,
    statusCode: x.statusCode as OrderItemStatusCode,
    valueWithTaxFormatted: x.valueWithTaxFormatted,
    valueWithTax: x.valueWithTax,
    reportName: x.reportName,
    identifierType: x.identifierType,
    identifier: x.identifier
  }))
}

export function mapOrderDataFromResponse({ data }: OrderResponse): OrderData | undefined {
  if (!data?.order) return undefined
  return {
    orderStatus: data.order.orderStatus as OrderStatus,
    orderType: data.order.orderType as OrderType,
    paymentStatus: data.order.paymentStatus ? (data.order.paymentStatus as PaymentStatus) : undefined,
    totalNetValueFormatted: data.order.totalNetValueFormatted,
    orderId: data.order.orderId,
    isAccountingError: !!data.order.accountingError,
    totalTaxValueFormatted: data.order.totalTaxValueFormatted,
    totalValueWithTaxFormatted: data.order.totalValueWithTaxFormatted,
    totalValueWithTax: data.order.totalValueWithTax,
    currency: data.order.currency,
    isAnyCustomerRequestError: data.order.isAnyCustomerRequestError
  }
}

export function* resetOrderMetadata(e: any) {
  if (axios.isAxiosError(e)) {
    const error = e as AxiosError

    if (error.response?.status === 404) {
      yield put(OrderActions.setOrderMetadata({ metadata: { id: undefined, itemCount: 0 } }))
      yield put(OrderActions.setOrderItems({ items: [] }))
    }

    if (error.response?.status === 400) {
      yield put(OrderActions.setOrderMetadata({ metadata: { id: undefined, itemCount: 0 } }))
      yield put(OrderActions.setOrderItems({ items: [] }))
    }
  } else {
    yield put(OrderActions.setOrderMetadata({ metadata: { id: undefined, itemCount: 0 } }))
    yield put(OrderActions.setOrderItems({ items: [] }))
  }
}

export function* loadOrder(action: ReturnType<typeof OrderActions.loadOrder>): any {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return

  const { page, pageSize } = action.payload
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'cart' })

  const orderId: string = yield select(getOrderId)
  const domainCode: string = yield select(getCountryCode)

  const requestParams = {
    domainCode,
    page,
    pageSize,
    sort: sortParam(sorting)
  }

  try {
    let orderResponse: AxiosResponse<OrderResponse> = yield call(apiCall, {
      url: '/api/ordering/order',
      method: 'get',
      params: {
        orderId,
        ...requestParams
      }
    })

    let order = mapOrderDataFromResponse(orderResponse.data)
    if (order && order.orderStatus != OrderStatus.Draft) {
      orderResponse = yield call(apiCall, {
        url: '/api/ordering/order',
        method: 'get',
        params: {
          ...requestParams
        }
      })
      order = mapOrderDataFromResponse(orderResponse.data)
    }

    if (order) {
      yield put(OrderActions.setOrderData({ order }))
      yield put(
        OrderActions.setOrderItems({
          items: mapOrderResponseItemsToOrderItems(orderResponse.data?.data?.order?.customerRequests)
        })
      )
      if (orderResponse.data.data?.meta?.pageNumber && orderResponse.data.data?.meta?.totalCount && pageSize) {
        yield put(
          TableActions.setPagination({
            tableKey: 'cart',
            pagination: {
              current: orderResponse.data.data.meta.pageNumber,
              pageSize: pageSize,
              total: orderResponse.data.data.meta.totalCount
            }
          })
        )
      }
    } else {
      yield call(resetOrderMetadata, {})
    }
  } catch (e) {
    yield call(resetOrderMetadata, e)
    yield put(OrderActions.setOrderError({ error: e }))
  }
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'cart') return

  const pageSize: number = yield select(getOrderPageSize)

  yield put(
    OrderActions.loadOrder({
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'cart') return

  const { pagination } = payload

  yield put(
    OrderActions.loadOrder({
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* removeOrderItem({ payload }: ReturnType<typeof OrderActions.removeOrderItem>) {
  const orderId: string = yield select(getOrderId)
  yield call(apiCall, {
    url: `/api/ordering/order/${orderId}/customerRequests/${payload.customerRequestId}`,
    method: 'delete'
  })

  yield spawn(loadOrderMetadata)
}

export function* loadOrderMetadata() {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) {
    yield put(OrderActions.setOrderMetadata({ metadata: { id: undefined, itemCount: 0 } }))
    return
  }

  try {
    const res: AxiosResponse<OrderMetadata> = yield call(apiCall, {
      url: '/api/ordering/order/meta',
      method: 'get'
    })

    if (res.data) yield put(OrderActions.setOrderMetadata({ metadata: res.data }))
  } catch (e) {
    yield put(OrderActions.setOrderMetadata({ metadata: { id: undefined, itemCount: 0 } }))
    yield call(resetOrderMetadata, e)
    yield put(OrderActions.setOrderError({ error: e }))
  }
}

function generateAddToCartEvent(gtmData: AddItemToCartGtmData) {
  const { currency, value, cartItemsCount, company, price, reportName, button } = gtmData

  const companyId = _.last(_.split(company?.companyIdentifier, '-', 2))

  addGtmEvent({
    event: GtmEventType.AddToCart,
    currency: currency || '',
    value: value || 0,
    items: [
      {
        ['item_id']: `${company ? companyId + '_' : ''}${reportName}`,
        ['item_name']: `${company ? company?.name + '_' : ''}${reportName}`,
        price: price || 0,
        quantity: 1
      }
    ],
    itemNumber: cartItemsCount + 1,
    button: `AddToCart${button}`
  })
}

function* handleError(e: any, maxItemCount?: number) {
  const { response } = e
  let errors: string[] = []

  if (_.isObject(response?.data?.errors)) {
    errors = _.reduce(response.data.errors, (acc, error) => [...acc, error], [] as string[])
  }
  yield put(LogActions.addLogItem(mapActionError(e, 'ERROR', window.location.href)))
  let errorMessage = _.isEmpty(errors) ? 'companyDetail:Reports.BuyErrorDefault' : 'companyDetail:Reports.BuyError'
  if (response.data.errors['179ZCC']) errorMessage = 'cart:ConsumptionLimitText'
  if (response.data.errors['110ZCC']) errorMessage = 'cart:ProductNotInContract'
  if (response.data.errors['CV014']) errorMessage = 'cart:MaximumItemsSupportdExceededText'

  yield put(
    DialogActions.showInfoDialog({
      i18nText: errorMessage,
      i18nTitle: 'investigation:ErrorTitle',
      namespaces: ['companyDetail', 'investigation', 'cart', 'cartItem'],
      textParam: errorMessage == 'cart:MaximumItemsSupportdExceededText' ? { maximumRecords: maxItemCount } : {}
    })
  )
}

export function* addToCart(action: ReturnType<typeof OrderActions.addToCart>) {
  const { formData, gtmData, navigate } = action.payload
  const maximumNumberOfSupportedItems: number = yield select(getMaximumNumberOfSupportedItems)
  const orderItemCount: number = yield select(getNumberOfOrderItems)

  try {
    if (maximumNumberOfSupportedItems > 0 && orderItemCount > 0 && orderItemCount >= maximumNumberOfSupportedItems) {
      yield put(
        DialogActions.showInfoDialog({
          i18nText: 'cart:MaximumItemsSupportdExceededText',
          i18nTitle: 'cart:MaximumItemsSupportExceededTitle',
          namespaces: ['cart'],
          textParam: { maximumRecords: maximumNumberOfSupportedItems }
        })
      )
    } else {
      yield call(apiCall, {
        url: '/api/ordering/order',
        method: 'post',
        data: {
          ...formData,
          orderType: OrderType.Normal
        }
      })

      generateAddToCartEvent(gtmData)

      yield call(loadOrderMetadata)

      const redirectToCartOnItemAdded: boolean = yield select(getRedirectToCartOnItemAdded)
      if (redirectToCartOnItemAdded) {
        const i18n: i18n = yield getContext('i18n')
        yield navigate(generateRoute(Routes.Cart, i18n.language, i18n.t))
      }
    }
  } catch (e) {
    yield handleError(e, maximumNumberOfSupportedItems)
  }
}

export function* buyNow(action: ReturnType<typeof OrderActions.buyNow>) {
  const { formData, gtmData, navigate } = action.payload

  try {
    const { data }: AxiosResponse<AddItemToCartResponse> = yield call(apiCall, {
      url: '/api/ordering/order',
      method: 'post',
      data: {
        ...formData,
        orderType: OrderType.BuyNow
      }
    })

    generateAddToCartEvent(gtmData)
    const i18n: i18n = yield getContext('i18n')
    yield navigate(generateRoute(Routes.Checkout, i18n.language, i18n.t, {}, false, { cartId: data?.data?.orderId }))
  } catch (e) {
    yield handleError(e)
  }
}

/* istanbul ignore next */
export function* orderSagas() {
  yield all([
    takeLatest(getType(OrderActions.loadOrder), withLoading('order', loadOrder)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeEvery(
      getType(OrderActions.removeOrderItem),
      withLoadingParametrized('order', removeOrderItem, x =>
        generateRemoveOrderItemLoadingId(x.payload.customerRequestId)
      )
    ),
    takeLatest(
      getType(OrderActions.loadOrderMetadata),
      withLoadingParametrized('order', loadOrderMetadata, () => _.constant('metaData'))
    ),
    takeEvery(
      getType(OrderActions.addToCart),
      withLoadingParametrized('order', addToCart, x =>
        generateAddToCartLoadingId(x.payload.companyId, x.payload.reportCode, 'addToCart')
      )
    ),
    takeEvery(
      getType(OrderActions.buyNow),
      withLoadingParametrized('order', buyNow, x =>
        generateAddToCartLoadingId(x.payload.companyId, x.payload.reportCode, 'buyNow')
      )
    )
  ])
}
